/**
 * Auth hooks: useCurrentUser (/auth/me), useLogin, useLogout.
 *
 * Login stores tokens in memory (auth-token) and flips the session flag; the
 * current user is fetched/seeded into the TanStack Query cache. `/auth/logout`
 * is not implemented server-side (no refresh-token revocation yet), so logout is
 * client-side only: drop tokens + clear authed caches.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { clearTokens, setTokens } from "@/lib/auth-token";
import { clearPrivateCaches, queryKeys } from "@/lib/query-client";
import type { CurrentUser } from "@/lib/permissions";
import { authApi, toCurrentUser, type LoginResponse } from "./auth.api";
import { useAuthStore } from "./auth.store";

export function useCurrentUser() {
  const status = useAuthStore((s) => s.status);
  return useQuery<CurrentUser>({
    queryKey: queryKeys.currentUser,
    queryFn: async () => toCurrentUser(await authApi.me()),
    enabled: status === "authenticated",
    staleTime: 5 * 60_000,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  const setAuthenticated = useAuthStore((s) => s.setAuthenticated);
  return useMutation<LoginResponse, unknown, { email: string; password: string }>({
    mutationFn: ({ email, password }) => authApi.login(email, password),
    onSuccess: (res) => {
      setTokens(res.accessToken, res.refreshToken);
      setAuthenticated();
      // Seed the cache so the UI reflects the user immediately.
      qc.setQueryData(queryKeys.currentUser, toCurrentUser(res.user));
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  const setAnonymous = useAuthStore((s) => s.setAnonymous);
  return () => {
    clearTokens();
    setAnonymous();
    // Drop the current user + every authenticated server data family
    // (shared with the terminal-401 paths).
    clearPrivateCaches(qc);
  };
}
